import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  DirectionsRun,
  EditNote,
  MonitorHeart,
  NotificationsNone,
  Settings,
  Spa,
} from '@mui/icons-material';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { tokens } from '../../theme';
import { MotionBox, editorialSerif } from './analytics/shared';
import FastingRecommendationsPanel from './FastingRecommendationsPanel';
import NutritionInsightsPanel from './NutritionInsightsPanel';
import landingForest from '../../assets/landing-forest.png';
import cardMountains from '../../assets/card-mountains.png';
import ctaBackground from '../../assets/cta-background.png';

const BORDER = 'rgba(197, 215, 152, 0.16)';
const BORDER_STRONG = 'rgba(197, 215, 152, 0.28)';
const SURFACE = 'rgba(10, 18, 14, 0.84)';
const PANEL = 'rgba(15, 24, 19, 0.92)';
const PANEL_SOFT = 'rgba(18, 29, 23, 0.82)';

const sliderSx = {
  color: '#e3cb92',
  height: 4,
  '& .MuiSlider-thumb': {
    width: 14,
    height: 14,
    backgroundColor: tokens.cream,
    border: `1px solid ${BORDER_STRONG}`,
  },
  '& .MuiSlider-rail': { opacity: 0.25, backgroundColor: 'rgba(255,255,255,0.14)' },
  '& .MuiSlider-mark': { width: 2, height: 2, borderRadius: '50%', backgroundColor: tokens.textMuted },
};

function BiometricSparkCard({ label, value, delta, dataKey, series, color }) {
  return (
    <Box sx={{ p: 0.9, border: `1px solid ${BORDER}`, backgroundColor: 'rgba(255,255,255,0.02)' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.35 }}>
        <Typography variant="caption" sx={{ color: tokens.textMuted }}>{label}</Typography>
        {delta && <Typography variant="caption" sx={{ color: tokens.accentStrong }}>{delta}</Typography>}
      </Stack>
      <Typography variant="body1" sx={{ color: tokens.cream, mb: 0.55, fontSize: '1.05rem' }}>{value}</Typography>
      <Box sx={{ height: 34 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={series}>
            <Area type="monotone" dataKey={dataKey} stroke={color} fill={color} fillOpacity={0.14} strokeWidth={1.6} dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

export default function UserDashboardHome({
  greeting,
  displayDate,
  selectedDate,
  displayMetric,
  fastingState,
  fastingPhases,
  activeFastingPhaseIndex,
  estimatedEndText,
  fastingStreak,
  bestFastingStreak,
  isDesktop,
  isWideBoard,
  dailyCalorieRequirement,
  nutritionMacroCards,
  mealShowcase,
  recentFoodDisplay,
  activityCards,
  chartData,
  biometricCards,
  biometricSeries,
  reflection,
  setReflection,
  getFoodVisual,
  summarizeMealEntries,
  userId,
  onLogFood,
  onViewFoodLog,
  onLogActivity,
  onManageFast,
  fastingRecommendations,
  onApplySuggestedProtocol,
  onOpenSettings,
  onOpenSupport,
  onQuickAddFood,
  onSaveReflection,
  onViewBiometrics,
}) {
  const displayDateObj = parseISO(displayDate);

  return (
    <MotionBox initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      {/* Hero + Fasting Arc unified panel */}
      <Box sx={{ overflow: 'hidden', border: `1px solid ${BORDER}`, backgroundColor: SURFACE, mb: 1.45 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 226px' },
            borderBottom: `1px solid ${BORDER}`,
            backgroundImage: `linear-gradient(180deg, rgba(7,16,13,0.12), rgba(7,16,13,0.52)), url(${landingForest})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            minHeight: { xs: 'auto', lg: 198 },
          }}
        >
          <Box sx={{ p: { xs: 2.1, md: 2.35 }, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            {isDesktop && (
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 1.6 }}>
                <Button
                  variant="outlined"
                  onClick={onLogFood}
                  sx={{
                    px: 2,
                    py: 0.75,
                    minWidth: 'auto',
                    borderRadius: 2,
                    borderColor: BORDER_STRONG,
                    color: tokens.cream,
                    backgroundColor: 'rgba(40, 51, 33, 0.68)',
                    textTransform: 'none',
                    '&:hover': { borderColor: tokens.accentStrong, backgroundColor: 'rgba(40, 51, 33, 0.82)' },
                  }}
                  startIcon={<Add sx={{ fontSize: 15 }} />}
                >
                  Log Quick Entry
                </Button>
                <IconButton onClick={onOpenSupport} sx={{ color: tokens.textSoft, border: `1px solid ${BORDER}`, borderRadius: 1.5, backgroundColor: 'rgba(7,16,13,0.36)' }} aria-label="Support">
                  <NotificationsNone fontSize="small" />
                </IconButton>
                <IconButton onClick={onOpenSettings} sx={{ color: tokens.textSoft, border: `1px solid ${BORDER}`, borderRadius: 1.5, backgroundColor: 'rgba(7,16,13,0.36)' }}>
                  <Settings fontSize="small" />
                </IconButton>
              </Stack>
            )}
            <Box>
              <Typography variant="body1" sx={{ color: tokens.textSoft, mb: 1 }}>{greeting}</Typography>
              <Typography
                variant="h1"
                sx={{
                  color: tokens.cream,
                  maxWidth: 500,
                  fontSize: { xs: '2.2rem', md: '2.58rem' },
                  lineHeight: 0.94,
                  fontFamily: editorialSerif,
                  fontWeight: 500,
                  letterSpacing: '-0.025em',
                }}
              >
                Small choices. Powerful momentum.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              p: { xs: 1.8, md: 1.95 },
              borderLeft: { lg: `1px solid ${BORDER}` },
              backgroundColor: 'rgba(7,16,13,0.42)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" sx={{ color: tokens.textSoft, mb: 0.5 }}>
              {format(displayDateObj, 'EEEE')}
            </Typography>
            <Typography variant="h3" sx={{ color: tokens.cream, mb: 1.4, fontFamily: editorialSerif, fontWeight: 500, fontSize: '2.05rem' }}>
              {format(displayDateObj, 'MMM d')}
            </Typography>
            <Typography variant="body2" sx={{ color: tokens.textMuted }}>
              {displayDate === selectedDate ? "Today's focus" : 'Recent focus'}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
              <Spa sx={{ color: tokens.accentStrong, fontSize: 16 }} />
              <Typography variant="h6" sx={{ color: tokens.accentStrong, fontWeight: 500 }}>
                Finish strong
              </Typography>
            </Stack>
          </Box>
        </Box>

        {/* Fasting Arc */}
        <Box sx={{ p: { xs: 1.8, md: 1.85 } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 200px 220px', lg: 'minmax(0, 1fr) 230px 255px' },
              gap: 0,
              alignItems: 'stretch',
            }}
          >
            <Box sx={{ pr: { xs: 0, md: 1.6, lg: 2.1 } }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.35, flexWrap: 'wrap' }}>
                <Stack direction="row" spacing={0.85} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                  <Typography variant="h3" sx={{ color: tokens.cream, fontSize: { xs: '2rem', md: '1.95rem' }, fontFamily: editorialSerif, fontWeight: 500 }}>
                    Fasting Arc
                  </Typography>
                  <Spa sx={{ color: tokens.accentStrong, fontSize: 16 }} />
                  <Typography variant="body2" sx={{ color: tokens.accentStrong }}>
                    {displayMetric?.fasting?.protocol || '16:8'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: tokens.textMuted }}>•</Typography>
                  <Typography variant="body2" sx={{ color: tokens.textMuted }}>
                    {fastingState.startedLabel}
                  </Typography>
                </Stack>
                <Chip
                  label={fastingState.phaseLabel}
                  onClick={onManageFast}
                  sx={{
                    height: 28,
                    borderRadius: 1.5,
                    border: `1px solid ${BORDER}`,
                    bgcolor: 'rgba(35, 44, 28, 0.72)',
                    color: '#e2b35f',
                    cursor: 'pointer',
                    '& .MuiChip-label': { px: 1.1 },
                  }}
                />
              </Stack>

              <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block', mb: 0.8, letterSpacing: '0.03em' }}>
                Phase
              </Typography>

              <Box sx={{ position: 'relative', mb: 1.25 }}>
                <Box sx={{ height: 6, background: 'rgba(194, 208, 162, 0.22)', overflow: 'hidden' }}>
                  <Box
                    sx={{
                      width: `${fastingState.progress}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #d4a26b 0%, #c0cd73 55%, #87985c 100%)',
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: `calc(${fastingState.progress}% - 9px)`,
                    transform: 'translateY(-50%)',
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: '1px solid #d8cf8d',
                    backgroundColor: 'rgba(95, 111, 65, 0.95)',
                    boxShadow: '0 0 0 2px rgba(10,18,14,0.65)',
                  }}
                />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 0.65 }}>
                {fastingPhases.map((phase, index) => (
                  <Box
                    key={phase.label}
                    sx={{
                      pr: 0.6,
                      borderRight: index < fastingPhases.length - 1 ? '1px solid rgba(197, 215, 152, 0.08)' : 'none',
                    }}
                  >
                    <Typography variant="body2" sx={{ color: index === activeFastingPhaseIndex ? tokens.accentStrong : tokens.textSoft, mb: 0.15 }}>
                      {phase.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: tokens.textMuted }}>{phase.window}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box
              sx={{
                px: { xs: 0, md: 1.35, lg: 1.65 },
                py: { xs: 1.4, md: 0.2 },
                borderLeft: { md: `1px solid ${BORDER}` },
                borderTop: { xs: `1px solid ${BORDER}`, md: 'none' },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography variant="body2" sx={{ color: tokens.textMuted, mb: 0.55 }}>Time Remaining</Typography>
              <Typography variant="h2" sx={{ color: tokens.cream, fontSize: { xs: '2.15rem', md: '2.35rem' }, mb: 0.25, fontFamily: editorialSerif, fontWeight: 500 }}>
                {fastingState.countdownText}
              </Typography>
              <Typography variant="body2" sx={{ color: tokens.textMuted, mb: 0.35 }}>Est. End</Typography>
              <Typography variant="body2" sx={{ color: tokens.cream }}>{estimatedEndText}</Typography>
              <Typography variant="caption" sx={{ color: tokens.accentStrong, mt: 0.6 }}>{fastingState.phaseCopy}</Typography>
            </Box>

            <Box
              sx={{
                p: 1.25,
                borderLeft: { md: `1px solid ${BORDER}` },
                borderTop: { xs: `1px solid ${BORDER}`, md: 'none' },
                backgroundColor: 'rgba(255,255,255,0.02)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.75 }}>
                <Typography variant="body2" sx={{ color: tokens.textMuted }}>Streak</Typography>
                <Typography variant="caption" sx={{ color: tokens.textMuted, letterSpacing: '0.22em' }}>M T W T F S S</Typography>
              </Stack>
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.85 }}>
                <Box sx={{ flexShrink: 0, minWidth: 58 }}>
                  <Typography variant="h2" sx={{ color: tokens.cream, lineHeight: 1, mb: 0.15, fontFamily: editorialSerif, fontWeight: 500 }}>
                    {fastingStreak}
                  </Typography>
                  <Typography variant="body2" sx={{ color: tokens.textMuted, lineHeight: 1.2 }}>days</Typography>
                </Box>
                <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(7, 10px)', gap: '6px 5px', justifyContent: 'end' }}>
                  {Array.from({ length: 28 }).map((_, index) => {
                    const activeCutoff = Math.min(fastingStreak, 21);
                    const isHighlight = index === Math.max(activeCutoff - 1, 0) && activeCutoff > 0;
                    return (
                      <Box
                        key={index}
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: index < activeCutoff ? 'rgba(198,214,127,0.68)' : 'rgba(255,255,255,0.1)',
                          border: isHighlight ? `1px solid ${tokens.cream}` : 'none',
                          boxShadow: isHighlight ? '0 0 0 2px rgba(198,214,127,0.12)' : 'none',
                        }}
                      />
                    );
                  })}
                </Box>
              </Stack>
              <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block', mb: 0.55 }}>
                Best: <Box component="span" sx={{ color: tokens.cream }}>{bestFastingStreak} days</Box>
              </Typography>
              <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                Streak <Box component="span" sx={{ color: tokens.cream }}>{fastingStreak} day run active</Box>
              </Typography>
            </Box>
          </Box>
          {!displayMetric?.fasting?.isActive && (
            <FastingRecommendationsPanel
              recommendations={fastingRecommendations}
              onApplyProtocol={onApplySuggestedProtocol}
            />
          )}
        </Box>
      </Box>

      {/* Nutrition + Activity row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isWideBoard ? '1.08fr 1.22fr' : '1fr',
          gap: 1.2,
          mb: 1.45,
        }}
      >
        <Card sx={{ backgroundColor: PANEL, borderRadius: 0, border: `1px solid ${BORDER}` }}>
          <CardContent sx={{ p: { xs: 1.9, md: 1.95 } }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="h3" sx={{ color: tokens.cream, fontSize: { xs: '2rem', md: '2.05rem' }, fontFamily: editorialSerif, fontWeight: 500 }}>
                Nutrition
              </Typography>
              <Spa sx={{ color: tokens.accentStrong, fontSize: 16 }} />
            </Stack>
            {userId && <NutritionInsightsPanel userId={userId} />}

            <Box sx={{ display: 'grid', gridTemplateColumns: isWideBoard ? '180px minmax(0, 1fr) 182px' : '1fr', gap: 1.1 }}>
              <Box>
                <Typography variant="body2" sx={{ color: tokens.textMuted, mb: 1.1 }}>Macros</Typography>
                {nutritionMacroCards.map(([label, value, target, suffix, barColor]) => {
                  const hasTarget = typeof target === 'number' && target > 0;
                  return (
                    <Box key={label} sx={{ mb: 1.15 }}>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: tokens.cream }}>{label}</Typography>
                        <Typography variant="body2" sx={{ color: tokens.textMuted }}>
                          {hasTarget ? `${Math.round(value)} / ${target}${suffix}` : `${Math.round(value)}${suffix} · goal —`}
                        </Typography>
                      </Stack>
                      {hasTarget && (
                        <LinearProgress
                          variant="determinate"
                          value={Math.min((value / target) * 100, 100)}
                          sx={{
                            height: 6,
                            borderRadius: 0,
                            backgroundColor: 'rgba(255,255,255,0.05)',
                            '& .MuiLinearProgress-bar': { borderRadius: 0, backgroundColor: barColor },
                          }}
                        />
                      )}
                    </Box>
                  );
                })}
                {dailyCalorieRequirement === null && (
                  <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                    Add your height and weight in the app to see a calorie goal.
                  </Typography>
                )}
              </Box>

              <Box sx={{ borderLeft: isWideBoard ? `1px solid ${BORDER}` : 'none', borderRight: isWideBoard ? `1px solid ${BORDER}` : 'none', px: isWideBoard ? 1.5 : 0 }}>
                <Typography variant="body2" sx={{ color: tokens.textMuted, mb: 1.1 }}>Meals</Typography>
                <Stack spacing={0.7}>
                  {mealShowcase.length > 0 ? (
                    mealShowcase.map((group) => {
                      const calories = group.entries.reduce((sum, entry) => sum + (entry.calories || 0), 0);
                      return (
                        <Box key={group.mealType} sx={{ p: 0.95, border: `1px solid ${BORDER}`, backgroundColor: 'rgba(255,255,255,0.02)' }}>
                          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.55 }}>
                            <Typography variant="body2" sx={{ color: tokens.cream }}>{group.label}</Typography>
                            <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                              {group.entries[0] ? format(new Date(group.entries[0].timestamp), 'h:mm a') : ''}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block', mb: 0.6, lineHeight: 1.5 }}>
                            {summarizeMealEntries(group.entries)}
                          </Typography>
                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                              {group.entries.length} item{group.entries.length > 1 ? 's' : ''}
                            </Typography>
                            <Typography variant="caption" sx={{ color: tokens.textMuted }}>{calories} kcal</Typography>
                          </Stack>
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant="body2" sx={{ color: tokens.textMuted }}>No meals logged yet.</Typography>
                  )}
                </Stack>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ color: tokens.textMuted, mb: 1.1 }}>Recent Foods</Typography>
                <Stack spacing={0.7}>
                  {recentFoodDisplay.map((entry) => {
                    const visual = getFoodVisual(entry);
                    return (
                      <Box
                        key={entry.foodId || entry.id}
                        onClick={() => onQuickAddFood(entry)}
                        sx={{ p: 0.8, border: `1px solid ${BORDER}`, backgroundColor: 'rgba(255,255,255,0.02)', cursor: 'pointer' }}
                      >
                        <Stack direction="row" spacing={0.85} alignItems="center">
                          <Box
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: '50%',
                              display: 'grid',
                              placeItems: 'center',
                              color: '#f8f0d9',
                              fontSize: '0.95rem',
                              background: visual.bg,
                              border: '1px solid rgba(255,255,255,0.1)',
                              flexShrink: 0,
                            }}
                          >
                            {visual.glyph}
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" sx={{ color: tokens.cream }} noWrap>{entry.foodName}</Typography>
                            <Typography variant="caption" sx={{ color: tokens.textMuted }}>
                              {(entry.quantity || 0)} {entry.unit || 'g'} • {entry.calories} kcal
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </Box>

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.15 }}>
              <Button variant="text" onClick={onViewFoodLog || onLogFood} sx={{ color: tokens.accentStrong, px: 0 }}>View Food Log →</Button>
              <Button variant="text" onClick={onLogFood} sx={{ color: tokens.accentStrong, px: 0 }}>Log food →</Button>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ backgroundColor: PANEL, borderRadius: 0, border: `1px solid ${BORDER}` }}>
          <CardContent sx={{ p: { xs: 1.9, md: 1.95 } }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="h3" sx={{ color: tokens.cream, fontSize: { xs: '2rem', md: '2.05rem' }, fontFamily: editorialSerif, fontWeight: 500 }}>
                Activity & Biometrics
              </Typography>
              <DirectionsRun sx={{ color: tokens.accentStrong, fontSize: 20 }} />
              <MonitorHeart sx={{ color: tokens.textSoft, fontSize: 19 }} />
            </Stack>

            <Typography variant="body2" sx={{ color: tokens.textMuted, mb: 0.85 }}>Today</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 0.8, mb: 1.05 }}>
              {activityCards.map((item) => (
                <Box
                  key={item.label}
                  onClick={onLogActivity}
                  sx={{
                    p: 0.9,
                    border: `1px solid ${BORDER}`,
                    backgroundColor: PANEL_SOFT,
                    cursor: onLogActivity ? 'pointer' : 'default',
                    '&:hover': onLogActivity ? { backgroundColor: 'rgba(255,255,255,0.04)' } : undefined,
                  }}
                >
                  <Stack direction="row" spacing={0.9} alignItems="center" sx={{ mb: 0.45 }}>
                    <Box sx={{ width: 28, height: 28, display: 'grid', placeItems: 'center', border: `1px solid ${BORDER}`, borderRadius: 1.2, backgroundColor: item.iconBg, flexShrink: 0 }}>
                      {item.icon}
                    </Box>
                    <Typography variant="body2" sx={{ color: tokens.textMuted }}>{item.label}</Typography>
                  </Stack>
                  <Typography variant="h4" sx={{ color: item.tone, fontSize: '1.8rem', mb: 0.1 }}>
                    {typeof item.value === 'number' ? item.value.toLocaleString() : item.value}
                    <Box component="span" sx={{ color: tokens.textSoft, fontSize: '0.95rem', ml: 0.35 }}>{item.suffix}</Box>
                  </Typography>
                  {item.goal && (
                    <Typography variant="body2" sx={{ color: tokens.textMuted, mb: 0.55 }}>{item.goal}</Typography>
                  )}
                  {item.progress != null && (
                    <LinearProgress
                      variant="determinate"
                      value={item.progress}
                      sx={{
                        height: 4,
                        borderRadius: 0,
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        '& .MuiLinearProgress-bar': { borderRadius: 0, backgroundColor: item.tone },
                      }}
                    />
                  )}
                </Box>
              ))}
            </Box>

            <Typography variant="body2" sx={{ color: tokens.textMuted, mb: 0.85 }}>Trends (7 Days)</Typography>
            <Box sx={{ height: 150, mb: 1.1 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
                  <XAxis dataKey="date" stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                  <YAxis stroke={tokens.textMuted} tickLine={false} axisLine={false} />
                  <RechartsTooltip contentStyle={{ backgroundColor: '#111915', border: `1px solid ${BORDER}`, color: tokens.cream }} />
                  <Line type="monotone" dataKey="steps" stroke={tokens.accentStrong} strokeWidth={2.2} dot={false} />
                  <Line type="monotone" dataKey="calories" stroke={tokens.clay} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="water" stroke="#8ec8d7" strokeWidth={1.8} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', xl: 'repeat(4, 1fr)' }, gap: 0.7, mb: 0.85 }}>
              {biometricCards.map((card) => (
                <BiometricSparkCard key={card.label} {...card} series={biometricSeries} />
              ))}
            </Box>
            <Button variant="text" onClick={onViewBiometrics} sx={{ color: tokens.accentStrong, px: 0 }}>View Biometrics →</Button>
          </CardContent>
        </Card>
      </Box>

      {/* Reflection */}
      <Card sx={{ backgroundColor: PANEL, borderRadius: 0, border: `1px solid ${BORDER}`, overflow: 'hidden' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '240px minmax(0, 1fr) 300px' } }}>
            <Box
              sx={{
                minHeight: 124,
                backgroundImage: `linear-gradient(180deg, rgba(7,16,13,0.08), rgba(7,16,13,0.46)), url(${ctaBackground})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderRight: { xl: `1px solid ${BORDER}` },
              }}
            />

            <Box sx={{ p: { xs: 1.7, md: 1.8 }, borderRight: { xl: `1px solid ${BORDER}` } }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.35 }}>
                <Typography variant="h3" sx={{ color: tokens.cream, fontSize: { xs: '2rem', md: '2rem' }, fontFamily: editorialSerif, fontWeight: 500 }}>
                  Reflection
                </Typography>
                <EditNote sx={{ color: tokens.accentStrong, fontSize: 20 }} />
              </Stack>
              <Typography variant="body2" sx={{ color: tokens.textMuted, mb: 0.75 }}>Evening check-in</Typography>
              <TextField
                multiline
                minRows={2}
                value={reflection.notes}
                onChange={(e) => setReflection((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="How did today feel? What worked? What needs attention tomorrow?"
                fullWidth
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0.9, mt: 1.05 }}>
                <Box>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                    <Typography variant="body2" sx={{ color: tokens.textMuted }}>Energy</Typography>
                    <Typography variant="caption" sx={{ color: tokens.accentStrong }}>{reflection.energy}/5</Typography>
                  </Stack>
                  <Slider value={reflection.energy} min={1} max={5} step={1} marks onChange={(_, v) => setReflection((prev) => ({ ...prev, energy: v }))} sx={sliderSx} />
                </Box>
                <Box>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                    <Typography variant="body2" sx={{ color: tokens.textMuted }}>Mood</Typography>
                    <Typography variant="caption" sx={{ color: tokens.accentStrong }}>{reflection.mood}/5</Typography>
                  </Stack>
                  <Slider value={reflection.mood} min={1} max={5} step={1} marks onChange={(_, v) => setReflection((prev) => ({ ...prev, mood: v }))} sx={{ ...sliderSx, color: '#8ec8d7' }} />
                </Box>
              </Box>
              <Button variant="text" onClick={onSaveReflection} sx={{ color: tokens.accentStrong, px: 0, mt: 1.1 }}>Save Reflection →</Button>
            </Box>

            <Box sx={{ p: { xs: 1.6, md: 1.75 }, backgroundColor: 'rgba(255,255,255,0.02)' }}>
              <Typography variant="body2" sx={{ color: tokens.textMuted, mb: 0.85 }}>Gratitude</Typography>
              <TextField
                multiline
                minRows={2}
                value={reflection.gratitude}
                onChange={(e) => setReflection((prev) => ({ ...prev, gratitude: e.target.value }))}
                placeholder="My health, time in nature, and supportive friends."
                fullWidth
              />
              <Box
                sx={{
                  mt: 1.45,
                  p: 1.25,
                  minHeight: 92,
                  border: `1px solid ${BORDER}`,
                  backgroundImage: `linear-gradient(180deg, rgba(7,16,13,0.42), rgba(7,16,13,0.66)), url(${cardMountains})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  display: 'flex',
                  alignItems: 'flex-end',
                }}
              >
                <Typography sx={{ color: tokens.cream, fontFamily: editorialSerif, fontStyle: 'italic', fontSize: '1.05rem', maxWidth: 180, lineHeight: 1.3 }}>
                  The summit is what drives us, but the climb itself is what matters.
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </MotionBox>
  );
}
